import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, ListGroup, Spinner, Alert, Modal, Form, Badge } from 'react-bootstrap';
import { BookText, Edit, Trash2, PlusCircle, ArrowLeft, Award, RotateCcw } from 'lucide-react';
import { fetchBookDetail, fetchChaptersForBook, createChapter, updateChapter, deleteChapter, submitQuizScore } from '../api/readerService'; // Import submitQuizScore
import ChapterEditor from '../components/ChapterEditor';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.bubble.css';
import { registerQuillCustoms } from '../utils/quillCustoms';
import QuizPlayer from '../components/QuizPlayer'; // Import the new QuizPlayer component
import axiosInstance from '../utils/axios';

// Register custom formats like your CustomVideo Blot
registerQuillCustoms();

const BookDetailPage = () => {
    const { bookId } = useParams();
    const navigate = useNavigate();
    const { user } = useSelector((state) => state.auth);

    const [book, setBook] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [selectedChapter, setSelectedChapter] = useState(null); // For viewing content
    const [editingChapter, setEditingChapter] = useState(null); // For editing { id?, title, content_delta, order }
    
    const [loadingBook, setLoadingBook] = useState(true);
    const [loadingChapters, setLoadingChapters] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null); // For success feedback

    const [showEditorModal, setShowEditorModal] = useState(false);
    const [editorContent, setEditorContent] = useState({ delta: null, html: '' });
    const [chapterTitle, setChapterTitle] = useState('');
    const [chapterOrder, setChapterOrder] = useState(0);
    const [isSavingChapter, setIsSavingChapter] = useState(false);
    
    const [showQuiz, setShowQuiz] = useState(false); // State to toggle quiz view
    const [quizResult, setQuizResult] = useState(null); // New state to hold final quiz score
    const [isSubmittingScore, setIsSubmittingScore] = useState(false);

    const canManageChapters = user && book && (user.is_superuser || (user.id === book.owner));

    const loadBookAndChapters = useCallback(async () => {
        setLoadingBook(true);
        setLoadingChapters(true);
        setError(null);
        try {
            const bookResponse = await fetchBookDetail(bookId);
            setBook(bookResponse.data);
            const chaptersResponse = await fetchChaptersForBook(bookId);
            const fetchedChapters = chaptersResponse.data.results || chaptersResponse.data;
            setChapters(fetchedChapters);
            if (fetchedChapters.length > 0) {
                setSelectedChapter(fetchedChapters[0]);
            } else {
                setSelectedChapter(null);
            }
        } catch (err) {
            setError(err.response?.data?.detail || "Failed to load book details or chapters.");
            console.error(err);
        } finally {
            setLoadingBook(false);
            setLoadingChapters(false);
        }
    }, [bookId]);

    useEffect(() => {
        loadBookAndChapters();
    }, [loadBookAndChapters]);

    const submitQuizScore = (quizId, scoreData) => {
        // scoreData should be an object like { total_questions: 10, correct_answers: 8 }
        return axiosInstance.post(`/books/quizzes/${quizId}/scores/`, scoreData);
    };

    const handleChapterSelect = (chapter) => {
        setShowQuiz(false); // Go back to chapter view
        setQuizResult(null); // Clear any previous quiz results
        setSelectedChapter(chapter);
        const contentArea = document.getElementById('chapter-content-display');
        if (contentArea) contentArea.scrollIntoView({ behavior: 'smooth' });
    };

    const handleAddNewChapter = () => {
        setEditingChapter({ book: bookId, title: '', content_delta: null, order: chapters.length }); // new chapter object
        setChapterTitle('');
        setEditorContent({ delta: null, html: '' });
        setChapterOrder(chapters.length > 0 ? Math.max(...chapters.map(c => c.order)) + 1 : 0);
        setShowEditorModal(true);
    };

    const handleEditChapter = (chapter) => {
        setEditingChapter(chapter); // chapter object with id
        setChapterTitle(chapter.title);
        setEditorContent({ delta: chapter.content_delta, html: '' }); // Assuming you only need delta for editing
        setChapterOrder(chapter.order);
        setShowEditorModal(true);
    };
    
    const handleEditorContentChange = (content) => {
        setEditorContent(content);
    };

    const handleSaveChapter = async () => {
        setIsSavingChapter(true);
        setError(null);
        const chapterData = {
            title: chapterTitle,
            content_delta: editorContent.delta,
            order: chapterOrder,
            // book: bookId, // This will be set by the view on create, or is part of chapter for update
        };

        try {
            if (editingChapter && editingChapter.id) { // Editing existing chapter
                await updateChapter(editingChapter.id, chapterData);
            } else { // Creating new chapter
                await createChapter(bookId, chapterData);
            }
            setShowEditorModal(false);
            setEditingChapter(null);
            loadBookAndChapters(); // Refresh book and chapters
        } catch (err) {
            setError(err.response?.data?.detail || err.response?.data || "Failed to save chapter.");
            console.error(err);
        } finally {
            setIsSavingChapter(false);
        }
    };

    const handleDeleteChapter = async (chapterId) => {
        if (window.confirm("Are you sure you want to delete this chapter?")) {
            try {
                await deleteChapter(chapterId);
                loadBookAndChapters(); // Refresh
                if (selectedChapter && selectedChapter.id === chapterId) {
                    setSelectedChapter(chapters.length > 0 ? chapters[0] : null);
                }
            } catch (err) {
                alert("Failed to delete chapter: " + (err.response?.data?.detail || err.message));
            }
        }
    };

    // const handleQuizComplete = async (scoreData) => {

    //     // setQuizResult(scoreData); // Immediately show results on the screen
    //     // if (!book?.quiz?.id) {
    //     //     console.error("No quiz ID found to submit score.");
    //     //     setError("Could not submit score: Quiz ID not found.");
    //     //     return;
    //     // }
    //     // setIsSubmittingScore(true);
    //     // setError(null);
    //     // setSuccessMessage(null);
    //     try {
    //         await submitQuizScore(book.quiz.id, scoreData);
    //         setSuccessMessage(`Quiz score of ${scoreData.correct_answers}/${scoreData.total_questions} was saved.`);
    //     } catch (err) {
    //         setError(err.response?.data?.detail || "Failed to submit quiz score.");
    //         console.error(err);
    //     }
    // };
    
    const handleRestartQuiz = () => {
        setQuizResult(null); // Clear previous results
        setShowQuiz(true); // Ensure quiz mode is active
        // The QuizPlayer's internal onRestart prop will handle resetting its state
    };

    const handleReturnToBook = () => {
        setQuizResult(null);
        setShowQuiz(false);
        setSuccessMessage(null); // Clear any lingering submission messages
        setError(null);
        // Default to showing the first chapter
        if (chapters.length > 0) {
            setSelectedChapter(chapters[0]);
        }
    };
    
    if (loadingBook) return <Container className="text-center mt-5"><Spinner animation="border" variant="primary" /><p className="mt-2">Loading book...</p></Container>;
    if (error && !book) return <Container className="mt-5"><Alert variant="danger">{error}</Alert></Container>;
    if (!book) return <Container className="mt-5"><Alert variant="warning">Book not found.</Alert></Container>;

    const renderMainContent = () => {
        if (showQuiz) {
            if (quizResult) {
                // --- Quiz Results View ---
                return (
                    <div className="text-center p-5 d-flex flex-column align-items-center justify-content-center h-100">
                        <Award size={64} className="text-success mb-3"/>
                        <h2 className="mb-3">Quiz Complete!</h2>
                        <p className="lead">Your Score:</p>
                        <h3 className="display-4 fw-bold mb-4">{quizResult.correct_answers} / {quizResult.total_questions}</h3>
                        
                        {isSubmittingScore && <div className="text-center my-3"><Spinner animation="border" size="sm" /><p className="d-inline ms-2 text-muted">Saving your score...</p></div>}
                        {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
                        {successMessage && <Alert variant="success" className="mt-3">{successMessage}</Alert>}
                        
                        <div className="mt-4">
                            <Button variant="primary" onClick={handleRestartQuiz} className="me-2">
                                <RotateCcw size={16} className="me-2" /> Retake Quiz
                            </Button>
                            <Button variant="outline-secondary" onClick={handleReturnToBook}>
                                <BookText size={16} className="me-2" /> Return to Book
                            </Button>
                        </div>
                    </div>
                );
            }
            // --- Quiz Player View ---
            return book.quiz?.quiz_data ? (
                <QuizPlayer 
                    quizData={JSON.parse(book.quiz.quiz_data)} 
                    // onComplete={handleQuizComplete} 
                    quizId={book.quiz.id}
                    onRestartQuiz={handleRestartQuiz} 
                />
            ) : (
                <Alert variant="warning">Quiz data is not available for this book.</Alert>
            );
        }
        
        // --- Chapter View or Welcome Message ---
        return selectedChapter ? (
            <>
                <h2 className="mb-3 border-bottom pb-2">{selectedChapter.title}</h2>
                <div className="quill-read-only">
                    <ReactQuill
                        value={selectedChapter.content_delta || ''}
                        readOnly={true}
                        theme="bubble"
                        modules={{ toolbar: false }}
                    />
                </div>
            </>
        ) : (
            <div className="text-center p-5 d-flex flex-column align-items-center">
                <BookText size={48} className="text-muted mb-3"/>
                <p className="text-muted">Select a chapter from the Table of Contents to begin reading.</p>
                {book.quiz && 
                    <Button variant="primary" size="lg" className="mt-3" onClick={() => { setShowQuiz(true); setQuizResult(null); }}>
                        <Award className="me-2"/> Start Quiz
                    </Button>
                }
            </div>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row>
                {/* Sidebar */}
                <Col md={4} lg={3} className="mb-4 mb-md-0">
                    <Link to="/books" className="btn btn-outline-secondary btn-sm mb-3 d-inline-flex align-items-center">
                        <ArrowLeft size={16} className="me-2" /> Back to Books
                    </Link>
                    <Card className="shadow-sm mb-4">
                        <Card.Header as="h5">{book.title}</Card.Header>
                        {book.cover_image_url && <Card.Img variant="top" src={book.cover_image_url} />}
                        <Card.Body>
                            <Card.Subtitle className="mb-2 text-muted">By: {book.author || 'N/A'}</Card.Subtitle>
                            <Card.Text className="small">{book.description || 'No description.'}</Card.Text>
                        </Card.Body>
                    </Card>

                    <Card className="shadow-sm">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Table of Contents</h6>
                            {canManageChapters && (
                            <Button variant="success" size="sm" onClick={handleAddNewChapter}>
                                <PlusCircle size={16} className="me-1" /> Add
                            </Button>)}
                        </Card.Header>
                        {loadingChapters ? (
                            <ListGroup variant="flush" className="text-center p-3"><Spinner animation="border" size="sm" /></ListGroup>
                        ) : (
                            <ListGroup variant="flush">
                                {chapters.map(chap => (
                                    <ListGroup.Item
                                        key={chap.id}
                                        onClick={() => handleChapterSelect(chap)}
                                        active={!showQuiz && selectedChapter?.id === chap.id}
                                        className="d-flex justify-content-between align-items-center"
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className="flex-grow-1 me-2" style={{ wordBreak: 'break-word' }}>
                                            {chap.title}
                                        </span>
                                        {canManageChapters && (
                                        <span className="ms-auto d-flex align-items-center flex-shrink-0">
                                            <Button variant="outline-primary" size="sm" className="p-1 me-1" onClick={(e) => { e.stopPropagation(); handleEditChapter(chap); }} title="Edit Chapter"><Edit size={14} /></Button>
                                            <Button variant="outline-danger" size="sm" className="p-1" onClick={(e) => { e.stopPropagation(); handleDeleteChapter(chap.id); }} title="Delete Chapter"><Trash2 size={14} /></Button>
                                        </span>
                                        )}
                                    </ListGroup.Item>
                                ))}
                                {/* Quiz Link */}
                                {book.quiz && (
                                    <ListGroup.Item
                                        action
                                        onClick={() => { setShowQuiz(true); setQuizResult(null); }}
                                        active={showQuiz}
                                        className="d-flex align-items-center fw-bold"
                                    >
                                        <Award size={18} className="me-2"/> <span>Take the Quiz</span>
                                    </ListGroup.Item>
                                )}
                            </ListGroup>
                        )}
                    </Card>
                </Col>

                {/* Main Content Area */}
                <Col md={8} lg={9}>
                    <Card className="shadow-sm" id="chapter-content-display">
                        <Card.Body style={{ minHeight: '80vh' }}>
                            {renderMainContent()}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Chapter Editor Modal (remains unchanged) */}
            {canManageChapters && <Modal show={showEditorModal} onHide={() => {setShowEditorModal(false); setEditingChapter(null);}} size="xl" backdrop="static" centered>
                <Modal.Header closeButton className="bg-light"><Modal.Title as="h5">{editingChapter?.id ? 'Edit Chapter' : 'Add New Chapter'}</Modal.Title></Modal.Header>
                <Modal.Body className="p-4">
                    <Alert variant="danger" show={!!error} onClose={() => setError(null)} dismissible>{error}</Alert>
                    <Form>
                        <Row>
                            <Col md={8}>
                                <Form.Group className="mb-3" controlId="chapterModalTitle">
                                    <Form.Label>Chapter Title <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="text" value={chapterTitle} onChange={(e) => setChapterTitle(e.target.value)} required />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3" controlId="chapterModalOrder">
                                    <Form.Label>Order <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="number" value={chapterOrder} onChange={(e) => setChapterOrder(parseInt(e.target.value, 10))} required min="0" />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3" controlId="chapterModalContent">
                            <Form.Label>Content</Form.Label>
                            <ChapterEditor initialValue={editingChapter?.content_delta || ''} onContentChange={handleEditorContentChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-light">
                    <Button variant="secondary" onClick={() => {setShowEditorModal(false); setEditingChapter(null);}}>Cancel</Button>
                    <Button variant="primary" onClick={handleSaveChapter} disabled={isSavingChapter}>
                        {isSavingChapter ? <><Spinner as="span" animation="border" size="sm" /> Saving...</> : 'Save Chapter'}
                    </Button>
                </Modal.Footer>
            </Modal>}
        </Container>
    );
};

export default BookDetailPage;